<template>
  <div
    class="node-options cursor-pointer"
    :class="{
      'node-options--selected': selected,
    }"
    @click="handleSelect"
  >
    <div
      class="node-options__header"
      :style="{ marginBottom: dataSource.type !== NodeType.Card ? '6px' : '0' }"
    >
      <div class="node-options__title">
        <component v-if="currentNode?.icon" :is="currentNode?.icon" class="node-options__icon" />
        <h5 class="heading-h7">
          <span v-if="dataSource.type != NodeType.WrongOptionAPI">
            {{ currentNode?.name || `${dataSource.order}. ${sectionName}` }}
          </span>
          <span v-else style="text-transform: none;">Opción error en conector de API</span>
          
          <span class="child__counter" v-if="dataSource.type === NodeType.List">
            ({{ totals?.sections }}/10 secciones - {{ totals?.items }}/10 ítems)
          </span>

          <span class="acronym">
            {{ nodeIdentifier }}
          </span>

          <!--Solo para TARJETAS-->
          <a-button
            v-if="dataSource.type === NodeType.CardSection && dataSource.picture"
            class="button-inline"
            type="link"
            @click.stop="handlePreview"
          >
            <PaperClipOutlined />
          </a-button>
          <links-attachments :dataSource="dataSource" @onPreview="handleShowMedia" />
        </h5>
      </div>
      <div
        class="node-options__actions"
      >
        <div 
          v-if="[NodeType.WrongOptionAPI, NodeType.OptionConsultApi].includes(dataSource.type)"
          class="node-options__actions--header"
        >
          <a-tooltip v-if="showInfo" placement="bottom">
            <template #title>
              <span>
                Recuerda que no se podrán agregar más mensajes en este y se resolverá el ticket.
              </span>
            </template>
            <InfoCircleOutlined class="node-message__icon--info" />
          </a-tooltip>
          <p
            class="info-2 node-options__next--action"
            :title="action"
          >
            {{ action }}
          </p>
        </div>
        <div v-if="![NodeType.WrongOptionAPI, NodeType.OptionConsultApi].includes(dataSource.type)">
          <a-tooltip :title="addTooltipText">
            <a-button
              type="link"
              class="button"
              :loading="addingNewNode"
              @click.stop="() => !disabled && $emit('onAddOption')"
              :disabled="disabled || disabledAddChild"
            >
              Agregar {{ childName }}
            </a-button>
          </a-tooltip>
          <a-divider class="divider" type="vertical" />

          <template v-if="dataSource.type === NodeType.ListSection">
            <a-dropdown :trigger="['click']" placement="topRight" :disabled="disabledChangeOrder">
              <a-button type="link" class="button" @click.stop :disabled="disabledChangeOrder">
                Cambiar de posición
              </a-button>

              <template #overlay v-if="quantityOrders !== 0">
                <a-menu>
                  <a-menu-item
                    v-for="item in quantityOrders"
                    :key="item"
                    @click="$emit('onChangeOrder', item)"
                    :disabled="item === order || !isAllowedChange"
                  >
                    {{ item === order ? `Posición ${item} (actual)` : `Posición ${item}` }}
                  </a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>

            <a-divider class="divider" type="vertical" />
          </template>

          <a-button
            type="link"
            :danger="!disabledDelete"
            class="button"
            @click.stop="() => !disabledDelete && $emit('onDelete')"
            :disabled="disabledDelete"
          >
            Eliminar
          </a-button>
        </div>
      </div>
    </div>
    <p
      v-if="dataSource.type !== NodeType.Card"
      class="body-2 node-options__description mrg-bottom-0"
      :title="`${dataSource.title} | ${description}`"
    >
      <span v-if="dataSource.type === NodeType.CardSection">
        <template v-if="dataSource.title">
          {{ dataSource.title }}
        </template>

        <a-divider v-if="dataSource.title && description" class="divider" type="vertical" />

        <template v-if="description">
          {{ description }}
        </template>
      </span>

      <span v-else>{{ dataSource.title || description }}</span>

      <span v-if="dataSource.title_button"> Botón principal: {{ dataSource.title_button }} </span>
    </p>
  </div>
</template>
<script>
import { ref } from "vue";
import { ProfileOutlined, PaperClipOutlined, InfoCircleOutlined } from "@ant-design/icons-vue";

import { BotActions, NodeType } from "@/app/chatbots/utils/enums";
import { NodeData } from "@/app/chatbots/utils/enums";
import { ChannelType } from "@/app/shared/utils/enums";
import LinksAttachments from "@/app/chatbots/components/molecules/LinksAttachments.vue";
import { AcronymByNode } from "@/app/chatbots/utils/texts";

export default {
  name: "OptionsNode",
  components: {
    ProfileOutlined,
    PaperClipOutlined,
    LinksAttachments,
    InfoCircleOutlined,
  },
  props: {
    order: { type: Number, default: 1 },
    description: { type: String, default: "Descripcion" },
    quantityOrders: { type: Number, required: false, default: 0 },
    selected: { type: Boolean, required: false, default: false },
    disabled: { type: Boolean, default: false },
    disabledChangeOrder: { type: Boolean, default: false },
    disabledAddChild: { type: Boolean, default: false },
    isAllowedChange: { type: Boolean, required: false, default: true },
    dataSource: { type: Object, default: () => ({}) },
    totals: Object,
    currentChannel: { type: Object, default: () => ({}) },
    addingNewNode: { type: Boolean, default: false },
    action: { type: String, default: "Finalizar chatbot" },
    showInfo: { type: Boolean, required: false, default: false },
    parent: { type: Object, default: () => ({}) },
  },
  setup() {
    const open = ref(false);

    return { NodeType, open };
  },
  computed: {
    currentNode() {
      return NodeData[this.dataSource.type];
    },
    childName() {
      if (this.dataSource.type === NodeType.Options) return "opción";
      if (this.dataSource.type === NodeType.Buttons) return "botón";
      if (this.dataSource.type === NodeType.List) return "sección";
      if (this.dataSource.type === NodeType.ListSection) return "item";
      if (this.dataSource.type === NodeType.Card) return "tarjeta";
      if (this.dataSource.type === NodeType.CardSection) return "botón";

      return "error";
    },
    addTooltipText() {
      if (this.dataSource.type === NodeType.Buttons)
        return this.currentChannel.id === ChannelType.Whatsapp
          ? "Puedes agregar máximo 3 botones."
          : "Puedes agregar máximo 13 botones.";
      if (this.dataSource.type === NodeType.List) return "Puedes agregar máximo 10 secciones.";
      if (this.dataSource.type === NodeType.ListSection)
        return "Puedes agregar máximo 10 Ítems por lista.";
      if (this.dataSource.type === NodeType.Card) return "Puedes agregar máximo 10 tarjetas.";
      if (this.dataSource.type === NodeType.CardSection)
        return "Puedes agregar máximo 3 botones por tarjeta.";

      return null;
    },
    sectionName() {
      if (this.dataSource.type === NodeType.ListSection) return `Sección`;
      if (this.dataSource.type === NodeType.CardSection) return `Tarjeta`;
      if (this.dataSource.type === NodeType.OptionConsultApi) return `validación_de_cliente - ${this.dataSource.referencial_name}`;
      return "error";
    },
    mediaData() {
      if (!this.dataSource || (!this.dataSource.media && !this.dataSource.wrong_answer_media))
        return;
      return {
        uid: "1",
        media: this.dataSource.media || this.dataSource.wrong_answer_media,
        name: this.dataSource.filename || this.dataSource.wrong_answer_media_filename,
        type: this.dataSource.media_type || this.dataSource.wrong_answer_media_type,
        url: this.dataSource.media_url || this.dataSource.wrong_answer_media_url,
        status: "done",
      };
    },
    /**
     * Deshabilita el eliminado para las tarjetas
     */
    disabledDelete() {
      return (
        [
          NodeType.CardSection,
          NodeType.Buttons,
          NodeType.List,
          NodeType.ListSection,
          NodeType.Options,
          NodeType.Card,
        ].includes(this.dataSource.type) &&
        (this.disabled || this.disabledChangeOrder || this.parent?.action == BotActions.ContinueConnectorApi)
      );
    },
    nodeIdentifier() {
      const acronym = AcronymByNode[this.dataSource.type];
      const nodeId = this.dataSource.id;

      if (!acronym) return "";

      return `(${acronym}${nodeId})`;
    },
  },
  methods: {
    handleSelect() {
      this.$emit("onSelect");
    },
    handlePreview() {
      console.log("CLICK EMIT");
      this.$emit("preview", this.dataSource.picture);
    },
    handleShowMedia(mediaData) {
      this.$emit("onPreviewMedia", mediaData);
    },
  },
};
</script>
<style lang="sass" scoped>
.node-options
  border: 1px solid $gray-5
  border-radius: 8px
  margin-bottom: 8px
  padding: 12px
  &__header
    display: flex
    justify-content: space-between
    gap: 10px
  &__title
    display: flex
  &__description
    text-align: left
    color: $gray-8
    display: inline-flex
    justify-content: space-between
    width: 100%
    text-overflow: ellipsis
    overflow: hidden
    text-wrap: nowrap

  &__next--action
    text-align: left
    text-overflow: ellipsis
    overflow: hidden
    text-wrap: nowrap
    font-style: italic

  &__icon
    font-size: 20px
    width: 20px
    color: $primary
    margin-right: 8px
  &__actions
    &--header
      display: flex
      gap: 4px
      color: $gray-7
  &__action
    &--danger
      color: $red-6
  &--selected
    border: 1px solid $primary
    background-color: $blue-1
  &:hover
    border: 1px solid $primary

.divider
  background-color: #D9D9D9

.cursor-pointer
  cursor: pointer

.child__counter
  color: $gray-9
  font-size: 12px
  font-style: italic
  font-weight: 400

.heading-h7
  color: $gray-9
  font-weight: 600
  font-size: 14px
  text-transform: capitalize

h5, p
  margin-bottom: 0

.button
  padding: 0
  height: 100%

  &-inline
    height: 22px
    padding: 0
    margin-left: 6px

img
  width: 100%
  border-radius: 12px

.acronym
  font-size: 12px
  font-weight: 400
  color: $text-tertiary
</style>
